/**
 * A proportional stacked segment bar (Story 4.3, UX segment-bar). Renders each
 * segment as a width-`%` slice of the summed total, tone-colored from the status
 * tokens. Status is NEVER color alone: the `aria-label` restates every segment's
 * label + count (and the caller also shows those counts as adjacent numeric
 * columns), so the bar is never a hover-only/visual-only signal. A zero total
 * renders a neutral empty track. Reduced-motion safe (no transition-only meaning).
 */
export interface SegmentBarSegment {
  tone: 'ok' | 'warn' | 'bad' | 'info';
  label: string;
  count: number;
}

export function SegmentBar({ segments }: { segments: SegmentBarSegment[] }): React.JSX.Element {
  const total = segments.reduce((sum, s) => sum + Math.max(0, s.count), 0);
  // Restate every segment's label + count regardless of the visual widths.
  const ariaLabel = segments.map((s) => `${s.label} ${s.count}`).join(', ');

  if (total === 0) {
    return (
      <div
        className="segment-bar"
        role="img"
        aria-label={`${ariaLabel} (no certificates)`}
        data-empty="true"
      />
    );
  }

  return (
    <div className="segment-bar" role="img" aria-label={ariaLabel}>
      {segments.map((s) =>
        s.count > 0 ? (
          <span
            key={s.label}
            className="segment-bar__seg"
            data-tone={s.tone}
            style={{ width: `${(s.count / total) * 100}%` }}
          />
        ) : null,
      )}
    </div>
  );
}
